import { Menu } from '@mantine/core';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useShallow } from 'zustand/shallow';

import { MenuActionItem } from 'app/modules/wallforming/components/menu-container/MenuActionItem';
import { usePlacementStore } from 'app/modules/wallforming/components/state-management/placement';

interface PointerPosition {
    x: number;
    y: number;
}

export const ArticleContextMenu = () => {
    const { t } = useTranslation();

    const {
        articleGroups,
        isContextMenuVisible,
        articleInstanceSelections,
        instanceToGroupMap,
        setIsContextMenuVisible
    } = usePlacementStore(
        useShallow((state) => ({
            articleGroups: state.articleGroups,
            isContextMenuVisible: state.isContextMenuVisible,
            articleInstanceSelections: state.articleInstanceSelections,
            instanceToGroupMap: state.instanceToGroupMap,
            setIsContextMenuVisible: state.setIsContextMenuVisible
        }))
    );

    const selectedInstanceIds = useMemo(() => Object.keys(articleInstanceSelections), [articleInstanceSelections]);
    const pointerPositionRef = useRef<PointerPosition>({ x: 0, y: 0 });
    const contextMenuRef = useRef<HTMLDivElement | null>(null);

    // Find the group id if all selected instances belong to the same group
    const selectedGroupId = (() => {
        if (selectedInstanceIds.length === 0) {
            return undefined;
        }
        const firstGroupId = instanceToGroupMap[selectedInstanceIds[0]];
        if (!firstGroupId) {
            return undefined;
        }
        const allSameGroup = selectedInstanceIds.every((id) => instanceToGroupMap[id] === firstGroupId);
        return allSameGroup ? firstGroupId : undefined;
    })();

    const handleOnWindowPointerMove = useCallback((e: MouseEvent) => {
        pointerPositionRef.current = { x: e.clientX, y: e.clientY };
    }, []);

    useEffect(() => {
        window.addEventListener('pointermove', handleOnWindowPointerMove);
        return () => window.removeEventListener('pointermove', handleOnWindowPointerMove);
    }, [handleOnWindowPointerMove]);

    useEffect(() => {
        if (!contextMenuRef.current) {
            return;
        }

        if (isContextMenuVisible) {
            contextMenuRef.current.style.left = `${pointerPositionRef.current.x}px`;
            contextMenuRef.current.style.top = `${pointerPositionRef.current.y}px`;
        }
    }, [isContextMenuVisible]);

    return (
        <div
            ref={contextMenuRef}
            style={{
                position: 'fixed',
                zIndex: 10000,
                visibility: isContextMenuVisible ? 'visible' : 'hidden'
            }}
            onPointerDown={(e) => e.stopPropagation()}
        >
            <Menu
                opened={isContextMenuVisible}
                position="bottom-start"
                shadow="md"
                onChange={(opened) => setIsContextMenuVisible(opened)}
            >
                <Menu.Target>
                    <div />
                </Menu.Target>
                <Menu.Dropdown>
                    {selectedGroupId && articleGroups[selectedGroupId] && (
                        <>
                            <Menu.Label>
                                {t('labelGroup')}: {articleGroups[selectedGroupId].name}
                            </Menu.Label>
                            <Menu.Divider />
                        </>
                    )}
                    <MenuActionItem.Transform />
                    <MenuActionItem.Rotate />
                    <Menu.Divider />
                    <MenuActionItem.Cut />
                    <MenuActionItem.Copy />
                    <MenuActionItem.Paste />
                    <Menu.Divider />
                    <MenuActionItem.Group />
                    <MenuActionItem.Ungroup />
                    <MenuActionItem.EditGroup />
                    <Menu.Divider />
                    <MenuActionItem.DiscardSelection />
                    <MenuActionItem.Delete />
                </Menu.Dropdown>
            </Menu>
        </div>
    );
};
